import { observable, action, makeObservable } from 'mobx';
import { RootStore } from 'stores';
import { IBrand } from 'types';
import { MESSAGES, toast, toastTypeEnum } from 'common/utils';
import { getApiErrorMessage } from 'services/utils';
import {
  addBrandService,
  editBrandService,
  deleteBrandService,
  getBrandsService,
  IaddBrandParams,
  IeditBrandParams
} from 'services/brand';

export type IInitialState = {
  data: {
    brands: IBrand[];
    limit: number;
    page: number;
    total: number;
    sortBy?: 'asc' | 'desc' | null;
    sortField?: string;
  };
  ui: {
    getBrandsLoading: boolean;
    addBrandsLoading: boolean;
    deleteBrandsLoading: boolean;
  };
};

export const initialState: IInitialState = {
  data: {
    brands: [],
    limit: 10,
    page: 1,
    total: 0
  },
  ui: {
    getBrandsLoading: false,
    addBrandsLoading: false,
    deleteBrandsLoading: false
  }
};

export class BrandStore {
  state: IInitialState = initialState;
  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      state: observable,
      getBrands: action,
      addBand: action,
      editBrand: action,
      deleteBrand: action,
      setState: action,
      reset: action
    });

    this.rootStore = rootStore;
    /* use this space to hydrate state from localStorage */
  }

  getBrands = async () => {
    try {
      this.setState({ ui: { ...this.state.ui, getBrandsLoading: true } });
      const { data } = await getBrandsService();
      this.setState({ data: { ...this.state.data, brands: data.data.brands } });
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, getBrandsLoading: false } });
    }
  };

  addBand = async (
    brandData: IaddBrandParams
  ): Promise<boolean | undefined> => {
    try {
      this.setState({ ui: { ...this.state.ui, addBrandsLoading: true } });
      await addBrandService(brandData);
      toast(toastTypeEnum.success, MESSAGES.brandAddSuccess);
      return true;
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, addBrandsLoading: false } });
    }
  };

  editBrand = async (
    brandData: IeditBrandParams
  ): Promise<boolean | undefined> => {
    try {
      this.setState({ ui: { ...this.state.ui, addBrandsLoading: true } });
      await editBrandService(brandData);
      toast(toastTypeEnum.success, MESSAGES.brandEditedSuccess);
      return true;
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, addBrandsLoading: false } });
    }
  };

  deleteBrand = async (brandId: string): Promise<boolean | undefined> => {
    try {
      this.setState({ ui: { ...this.state.ui, deleteBrandsLoading: true } });
      await deleteBrandService(brandId);
      toast(toastTypeEnum.success, MESSAGES.brandDeletedSuccess);
      return true;
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, deleteBrandsLoading: false } });
    }
  };

  setState = (params: Partial<IInitialState>) => {
    const { state } = this;
    this.state = {
      ...state,
      ...params
    };
    /* use this space to save/sync state with localStorage */
  };

  reset = () => {
    this.state = initialState;
    /* use this space to reset localStorage in case if you're persisting this state */
  };
}
